import { writable, get } from 'svelte/store';
import api from './api.js';

/**
 * Strategy parameter policy, served by `GET /api/strategies` (TASK-20260819 / ADR-0024 §2).
 *
 * Before this, the client kept its own hand-synced copy of which parameters were frozen — a third copy
 * alongside the server validator and each strategy's own `OverrideParameters`. Everything here comes
 * from the strategy's declaration, so the UI locks exactly the fields the API rejects.
 *
 * **Fail closed.** Every accessor returns "no policy known" until a load succeeds, and forms gate submit
 * on `policyLoadState === 'loaded'`. A permissive fallback would re-open the pair picker for a
 * self-universe strategy and let the operator build a request the boundary 422s — the UI disabling is
 * an affordance, but a WRONG affordance is worse than none.
 */

/** `{ [strategyId]: { id, key, selectsOwnUniverse, forcedParameters[] } }` */
export const strategyPolicies = writable({});

/** 'idle' | 'loading' | 'loaded' | 'error' — only 'loaded' may enable a submit button. */
export const policyLoadState = writable('idle');

let inFlight = null;

/**
 * Fetches policy once. Safe to call from several components' onMount: concurrent callers share the
 * in-flight promise, a successful load is not refetched, and a failure leaves the state retryable.
 */
export async function loadStrategyPolicies() {
  if (get(policyLoadState) === 'loaded') return;
  if (inFlight) return inFlight;

  policyLoadState.set('loading');
  inFlight = (async () => {
    try {
      const list = await api.getStrategyPolicies();
      const byId = {};
      for (const entry of list ?? []) {
        byId[entry.id] = entry;
      }
      strategyPolicies.set(byId);
      policyLoadState.set(byId && Object.keys(byId).length > 0 ? 'loaded' : 'error');
    } catch {
      // Deliberately no permissive fallback — see the fail-closed note above.
      strategyPolicies.set({});
      policyLoadState.set('error');
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** The strategy's policy, or null when policy is unknown (not loaded, or the strategy is absent). */
export function policyFor(strategyId) {
  if (get(policyLoadState) !== 'loaded') return null;
  return get(strategyPolicies)[strategyId] ?? null;
}

/** True only when policy is loaded AND says the strategy picks its own universe. */
export function selectsOwnUniverse(strategyId) {
  return policyFor(strategyId)?.selectsOwnUniverse === true;
}

/** The strategy's forced parameters (`[{ name, display, reason }]`), or [] when policy is unknown. */
export function forcedParamsFor(strategyId) {
  return policyFor(strategyId)?.forcedParameters ?? [];
}

/**
 * The only candle frequency this strategy runs at (seconds), or null when any is valid
 * (TASK-20260821-shishito-frequency-lock).
 *
 * Null when policy has not loaded, exactly like the other readers here: locking a selector on a guess
 * would force a value the operator never chose and the server may not require.
 */
export function requiredFrequencyFor(strategyId) {
  return policyFor(strategyId)?.requiredFrequencySeconds ?? null;
}

/** Test seam: clears module state between cases. */
export function __resetPolicyForTests() {
  strategyPolicies.set({});
  policyLoadState.set('idle');
  inFlight = null;
}
